// client/src/pages/MySellerAuctions.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Stack, Alert, CircularProgress,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = { active: 'success', pending: 'warning', ended: 'default', sold: 'info' };

// Listing a new auction now always starts from a real inventory item — see
// the "List for Auction" action on the Inventory page — so this page is
// read-only: it just tracks auctions already listed and lets you cancel one.
const MySellerAuctions = ({ onNavigate }) => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);

  const fetchAuctions = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/seller/auctions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch your auctions.');
      setAuctions(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAuctions(); }, [fetchAuctions]);

  const handleCancelAuction = async (auctionId) => {
    if (!window.confirm('Cancel this auction? This only works if it has no bids yet.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller/auctions/${auctionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to cancel auction.');
      fetchAuctions();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>My Auctions</Typography>
        <Button variant="outlined" onClick={() => onNavigate?.('seller-inventory')}>Go to Inventory</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {auctions.length === 0 ? (
        <EmptyState
          icon={<GavelIcon />}
          title="You haven't listed any items for auction yet"
          description="Head to your Inventory page and use “List for Auction” on any product to start collecting live bids."
          actionLabel="Go to Inventory"
          onAction={() => onNavigate?.('seller-inventory')}
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell><TableCell>Current Price</TableCell><TableCell>Bids</TableCell>
                <TableCell>Ends</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auctions.map((auction) => (
                <TableRow key={auction.id} hover>
                  <TableCell><Link to={`/auctions/${auction.id}`}>{auction.product_name}</Link></TableCell>
                  <TableCell>{formatCurrency(auction.current_price)}</TableCell>
                  <TableCell>{auction.bid_count}</TableCell>
                  <TableCell>{new Date(auction.end_time).toLocaleString()}</TableCell>
                  <TableCell><Chip label={auction.status} size="small" color={statusColor[auction.status] || 'default'} /></TableCell>
                  <TableCell align="right">
                    {auction.status !== 'ended' && auction.status !== 'sold' && Number(auction.bid_count) === 0 && (
                      <Button size="small" color="error" onClick={() => handleCancelAuction(auction.id)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MySellerAuctions;
